import { StateField } from "@codemirror/state";
import { showTooltip, type Tooltip } from "@codemirror/view";
import type { BbelFunctionInfo } from "@/types/bbel-types";
import { insideSlot } from "./bbel-autocomplete";

export interface ActiveSignature {
  fn: BbelFunctionInfo;
  /** Zero-based index of the argument the cursor is currently on. */
  activeParam: number;
  /** Document offset of the call's opening `(` — a stable anchor while typing arguments. */
  anchor: number;
}

/**
 * The function call the cursor sits inside, and which argument it's on (#5683 B3a). Scans left from the
 * cursor tracking parenthesis depth and counting top-level commas; the identifier before the enclosing
 * `(` is the function. Active only inside a `{{ … }}` slot. Heuristic (no full parse) — a comma or paren
 * inside a string literal can mis-count the active argument, an accepted edge case for an advisory hint.
 */
export function activeSignature(before: string, functions: BbelFunctionInfo[]): ActiveSignature | null {
  if (!insideSlot(before)) return null;

  let depth = 0;
  let commas = 0;
  let i = before.length - 1;
  for (; i >= 0; i--) {
    const ch = before[i];
    if (ch === ")") depth++;
    else if (ch === "(") {
      if (depth === 0) break;
      depth--;
    } else if (ch === "," && depth === 0) {
      commas++;
    }
  }
  if (i < 0) return null; // no enclosing '('

  // The identifier immediately before the '(' (skipping whitespace) is the function name.
  let j = i - 1;
  while (j >= 0 && /\s/.test(before[j])) j--;
  const nameEnd = j + 1;
  while (j >= 0 && /[\w$]/.test(before[j])) j--;
  const name = before.slice(j + 1, nameEnd);

  const fn = name ? functions.find((f) => f.name === name) : undefined;
  if (!fn || fn.params.length === 0) return null;

  return { fn, activeParam: commas, anchor: i };
}

function renderSignature(sig: ActiveSignature): HTMLElement {
  const dom = document.createElement("div");
  dom.className = "bbel-sig";

  const code = document.createElement("code");
  code.className = "bbel-sig-code";
  code.append(document.createTextNode(`${sig.fn.name}(`));
  sig.fn.params.forEach((p, idx) => {
    if (idx) code.append(document.createTextNode(", "));
    const span = document.createElement("span");
    span.textContent = `${p.variadic ? "..." : ""}${p.name}${p.optional ? "?" : ""}${p.type ? `: ${p.type}` : ""}`;
    // A variadic last parameter stays active for every trailing argument.
    if (idx === sig.activeParam || (p.variadic && sig.activeParam >= idx)) span.className = "bbel-sig-active";
    code.append(span);
  });
  code.append(document.createTextNode(")"));

  const summary = document.createElement("div");
  summary.className = "bbel-sig-sum";
  summary.textContent = sig.fn.summary;

  dom.append(code, summary);
  return dom;
}

/**
 * CodeMirror signature-help: while the cursor is inside a known function's call parens, show a tooltip
 * with the signature and the active parameter bolded. The tooltip is rebuilt only when the function,
 * call position, or active argument changes (so typing within one argument doesn't remount it).
 */
export function bbelSignatureHelp(getFunctions: () => BbelFunctionInfo[]) {
  return StateField.define<{ tip: Tooltip; key: string } | null>({
    create: () => null,
    update(value, tr) {
      if (!tr.docChanged && !tr.selection) return value;

      const head = tr.state.selection.main.head;
      const sig = activeSignature(tr.state.sliceDoc(0, head), getFunctions());
      if (!sig) return null;

      const key = `${sig.fn.name}:${sig.anchor}:${sig.activeParam}`;
      if (value?.key === key) return value;

      return { key, tip: { pos: sig.anchor, above: true, create: () => ({ dom: renderSignature(sig) }) } };
    },
    provide: (f) => showTooltip.from(f, (v) => v?.tip ?? null),
  });
}
